import React, { useMemo, useCallback, useState } from 'react';
import { DataGrid, esES } from '@material-ui/data-grid';
import { Box, Button, Chip, IconButton, Typography } from '@material-ui/core';
import Modal from '@material-ui/core/Modal';
import { withStyles } from '@material-ui/core/styles';
import { AlertTriangle, Pencil, ClipboardList, Plus, Trash, X } from 'tabler-icons-react';
import { useCalidad } from './CalidadContext';

const DefectoChip = withStyles({ root: { backgroundColor: '#E7D4FF', color: '#000' } })(
    (props) => <Chip color="default" size="small" {...props} />
);

function formatHora(isoString) {
    if (!isoString) return '—';
    const date = new Date(isoString);
    const h = date.getHours() % 12 || 12;
    const m = String(date.getMinutes()).padStart(2, '0');
    const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
    return `${h}:${m} ${ampm}`;
}

function CustomNoRowsOverlay() {
    return (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography variant="body2" color="textSecondary">
                Sin registros de calidad en esta corrida
            </Typography>
        </Box>
    );
}

export default function CalidadTable() {
    const { registros, selectedCorrida, abrirNuevo, abrirEditar, eliminarRegistro, catalogoDefectos, catalogoCausas } = useCalidad();

    const getDefectoLabel = useCallback((codigo) => {
        const d = catalogoDefectos.find((x) => x.codigo === codigo);
        return d ? d.label : `Defecto ${codigo}`;
    }, [catalogoDefectos]);

    const getCausaLabel = useCallback((codigo) => {
        const c = catalogoCausas.find((x) => x.codigo === codigo);
        return c ? c.label : codigo ? `Causa ${codigo}` : '';
    }, [catalogoCausas]);

    const handleEditar = useCallback(
        (registro) => () => abrirEditar(registro),
        [abrirEditar]
    );

    const [deleteTarget, setDeleteTarget] = useState(null);

    const handleEliminar = useCallback(
        (row) => () => setDeleteTarget(row),
        []
    );

    const confirmarEliminar = useCallback(() => {
        if (deleteTarget) eliminarRegistro(deleteTarget.folio);
        setDeleteTarget(null);
    }, [deleteTarget, eliminarRegistro]);

    const cancelarEliminar = useCallback(() => setDeleteTarget(null), []);

    const columns = useMemo(() => [
        {
            field: 'folio',
            headerName: 'Folio',
            minWidth: 70,
            flex: 0.6,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'defecto',
            headerName: 'Tipo de defecto',
            minWidth: 130,
            flex: 1.4,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => <DefectoChip label={getDefectoLabel(params.value)} />,
        },
        {
            field: 'causaRaiz',
            headerName: 'Causa raíz',
            minWidth: 100,
            flex: 1.1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => getCausaLabel(params.value) || <span className="calidad-cell-empty">—</span>,
        },
        {
            field: 'cantidad',
            headerName: 'Cant.',
            minWidth: 60,
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'loteoSerie',
            headerName: 'N° Serie/Lote',
            minWidth: 110,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => params.value || <span className="calidad-cell-empty">—</span>,
        },
        {
            field: 'notas',
            headerName: 'Observaciones',
            minWidth: 140,
            flex: 2,
            align: 'left',
            headerAlign: 'center',
            sortable: false,
            renderCell: (params) => params.value || <span className="calidad-cell-empty">—</span>,
        },
        {
            field: 'usuario',
            headerName: 'Operador',
            minWidth: 100,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'fechaHora',
            headerName: 'Hora',
            minWidth: 80,
            flex: 0.7,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => formatHora(params.value),
        },
        {
            field: '_acciones',
            headerName: '',
            width: 90,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div className="calidad-table-actions">
                    <IconButton size="small" onClick={handleEditar(params.row)}>
                        <Pencil size={16} />
                    </IconButton>
                    <IconButton size="small" onClick={handleEliminar(params.row)}>
                        <Trash size={16} />
                    </IconButton>
                </div>
            ),
        },
    ], [handleEditar, handleEliminar, getDefectoLabel, getCausaLabel]);

    if (!selectedCorrida) {
        return (
            <div className="calidad-table-card">
                <div className="calidad-empty-state">
                    <div className="calidad-empty-state-icon">
                        <ClipboardList size={56} />
                    </div>
                    <p className="calidad-empty-state-text">Selecciona una corrida para comenzar</p>
                    <p className="calidad-empty-state-sub">Usa los filtros del encabezado para elegir línea y corrida</p>
                </div>
            </div>
        );
    }

    return (
        <div className="calidad-table-card">
            <div className="calidad-table-card-header">
                <p className="calidad-table-section-title">Registros de calidad</p>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<Plus size={16} />}
                    onClick={abrirNuevo}
                >
                    Registrar pieza
                </Button>
            </div>
            <div className="calidad-table-wrapper">
                <DataGrid
                    disableColumnFilter
                    disableDensitySelector
                    disableColumnMenu
                    checkboxSelection={false}
                    columns={columns}
                    rows={registros}
                    getRowId={(row) => row.folio}
                    localeText={esES.props.MuiDataGrid.localeText}
                    hideFooterPagination
                    hideFooter
                    components={{ NoRowsOverlay: CustomNoRowsOverlay }}
                />
            </div>

            <Modal open={deleteTarget !== null} onClose={cancelarEliminar}>
                <div className="usuarios-modal calidad-confirm-modal">
                    <div className="usuarios-modal-header">
                        <h2 className="usuarios-modal-title">Eliminar registro</h2>
                        <IconButton size="small" onClick={cancelarEliminar}>
                            <X size={18} strokeWidth={2} />
                        </IconButton>
                    </div>
                    <div className="usuarios-modal-body usuarios-modal-body--confirm">
                        <div className="calidad-confirm-icon">
                            <AlertTriangle size={40} />
                        </div>
                        <p className="calidad-confirm-text">
                            ¿Estás seguro de que deseas eliminar el registro
                            <strong>{` #${deleteTarget?.folio}`}</strong> — <strong>{getDefectoLabel(deleteTarget?.defecto)}</strong>?
                            Esta acción no se puede deshacer.
                        </p>
                    </div>
                    <div className="usuarios-modal-footer">
                        <Button
                            variant="outlined"
                            className="usuarios-modal-cancel"
                            onClick={cancelarEliminar}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            className="calidad-confirm-delete"
                            onClick={confirmarEliminar}
                        >
                            Eliminar
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
